import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import { getImageFromS3 } from '../../../imageService';
import mgmService from '../../../services/mgmService';
import CertificatePhoneComponent from '../certificate-phone-component';

const toNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getMonthlyRemaining = (balanceData) => {
  if (!balanceData) return null;

  const remaining = toNumber(
    balanceData.monthlyRemaining ?? balanceData.monthly_remaining
  );
  if (remaining !== null) return remaining;

  const limit = toNumber(balanceData.monthlyLimit ?? balanceData.monthly_limit);
  const spent = toNumber(balanceData.monthlySpent ?? balanceData.monthly_spent);
  if (limit !== null && spent !== null) return limit - spent;

  return null;
};

const CertificateComponent = ({
  selectedCertificate,
  setScreenNumber,
  onQuestionClick,
  onBought
}) => {
  const [isOpenChangeModal, setIsOpenChangeModal] = useState(false);
  const [balance, setBalance] = useState(0);
  const [monthlyRemaining, setMonthlyRemaining] = useState(null);
  const [isBuying, setIsBuying] = useState(false);
  const [buyError, setBuyError] = useState(false);
  const [mgmUser, setMgmUser] = useState(null);
  const [isPhoneStep, setIsPhoneStep] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await mgmService.getMe();
        setMgmUser(data);
        sessionStorage.setItem('mgm_me', JSON.stringify(data));
      } catch (e) {
        setMgmUser(null);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const balanceData = await mgmService.getBalance();
      setBalance(balanceData?.balance || 0);
      setMonthlyRemaining(getMonthlyRemaining(balanceData));
    })();
  }, []);

  const cost = Number(
    selectedCertificate?.points ?? selectedCertificate?.point_nominal ?? 0
  );
  const isWithinMonthlyLimit =
    monthlyRemaining === null || cost <= monthlyRemaining;
  const isExchangeAvailable =
    !(cost > 0) || (balance >= cost && isWithinMonthlyLimit);

  const buyAndProceed = async (number) => {
    if (isBuying || !selectedCertificate?.id) return;

    try {
      setIsBuying(true);
      setBuyError(false);

      await mgmService.buyCertificate(selectedCertificate.id, number);
      const rewardsList = await mgmService.getRewards();

      const boughtReward = rewardsList?.find(
        (item) => item.certificate_id === selectedCertificate.id
      );

      if (!boughtReward) setScreenNumber(100);

      setIsBuying(false);
      setIsOpenChangeModal(false);
      setIsPhoneStep(false);

      onBought(boughtReward);
    } catch (e) {
      setBuyError(true);
      setIsBuying(false);
      setScreenNumber(100);
    }
  };

  const changeButtonClick = () => {
    if (isBuying || !selectedCertificate?.id || !isExchangeAvailable) return;

    // If the user already has a phone number on file, keep the current flow.
    // Otherwise ask for the phone number on a dedicated screen and pass it along.
    if (mgmUser?.phone) {
      buyAndProceed();
    } else {
      setIsOpenChangeModal(false);
      setIsPhoneStep(true);
    }
  };

  if (isPhoneStep) {
    return (
      <CertificatePhoneComponent
        onSubmit={(number) => buyAndProceed(number)}
        onClose={() => setIsPhoneStep(false)}
        onQuestionClick={onQuestionClick}
        isSubmitting={isBuying}
      />
    );
  }

  return (
    <div className={styles.container}>
      <img
        src={getImageFromS3('/images/mgm/bg.png')}
        alt="background"
        className={styles.backgroundImage}
      />
      <img
        className={styles.glassHeadBg}
        src={getImageFromS3('/images/mgm/glass-head-bg.png')}
        alt="glass-head"
      />
      <div className={styles.content}>
        <div className={styles.header}>
          <svg
            width="20"
            height="18"
            viewBox="0 0 20 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => setScreenNumber(4)}
          >
            <path
              d="M4.29675 8.59375L9.66769 13.9647C10.261 14.5579 10.261 15.5198 9.66769 16.1131C9.07444 16.7063 8.11258 16.7063 7.51932 16.1131L0.707108 9.30086C0.316583 8.91033 0.316583 8.27717 0.707107 7.88664L7.51932 1.07443C8.11258 0.481175 9.07444 0.481175 9.66769 1.07443C10.261 1.66769 10.261 2.62955 9.66769 3.22281L4.29675 8.59375Z"
              fill="white"
            />
            <rect
              x="2.78076"
              y="6.99902"
              width="17"
              height="3"
              rx="1.5"
              fill="white"
            />
          </svg>
          <div onClick={onQuestionClick}>
            Є питання?
            <svg
              width="27"
              height="21"
              viewBox="0 0 27 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.86207 0C0.837931 0 0 0.821739 0 1.82609V16.4348L2.7931 13.6957H20.4828C21.5069 13.6957 22.3448 12.8739 22.3448 11.8696V1.82609C22.3448 0.821739 21.5069 0 20.4828 0H1.86207ZM23.2759 7.30435V11.8696C23.2759 13.4217 22.0655 14.6087 20.4828 14.6087H13.0345V16.4348C13.0345 17.4391 13.8724 18.2609 14.8966 18.2609H24.2069L27 21V9.13043C27 8.12609 26.1621 7.30435 25.1379 7.30435H23.2759Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div className={styles.pointBalanceContainer}>
          <p>
            Баланс балів <b>{balance}</b>
          </p>
        </div>
        <h1>Обміняти бали</h1>
        <div className={styles.cardHeader}>
          <img
            className={styles.glass}
            src={selectedCertificate?.glass}
            alt="glass"
          />
          <img
            className={styles.logo}
            src={selectedCertificate?.logo}
            alt="logo"
          />
          <div className={styles.amount}>
            {selectedCertificate.value}{' '}
            <span>{selectedCertificate.currency}</span>
          </div>
        </div>
        <p className={styles.certificateTitle} style={{ marginTop: 50 }}>
          Сертифікат на {selectedCertificate.value}{' '}
          <span>{selectedCertificate.currency}</span>
        </p>
        <p className={styles.certificateTitle} style={{ marginTop: 14 }}>
          Як скористатися сертифікатом?
        </p>
        <p
          className={styles.certificateTitle}
          style={{ marginTop: 14, fontWeight: 400 }}
        >
          Обмінюйте накопичені бали на обраний сертифікат у платформі.
          <br />
          Після обміну сертифікат буде доступний у вашому особистому кабінеті та
          автоматично прив’язаний до вашого номера телефону.
          <br />
          Використати сертифікат можна протягом строку його дії онлайн або в
          офлайн-магазинах партнера для обміну на товари відповідно до умов
          мережі.
        </p>
        <p
          className={styles.certificateTitle}
          style={{ marginTop: 10, fontWeight: 400 }}
        >
          Максимальна сума обміну – до 2 100 грн на місяць.
        </p>
        <div className={styles.changeContainer}>
          <p className={styles.title}>
            Вартість сертифікату{' '}
            <b>{selectedCertificate.point_nominal} балів</b>
          </p>
          <button
            className={`${styles.changeButton} ${
              isExchangeAvailable ? '' : styles.changeButtonDisabled
            }`}
            onClick={() => isExchangeAvailable && setIsOpenChangeModal(true)}
            disabled={!isExchangeAvailable}
          >
            Обміняти
          </button>
        </div>
      </div>
      {isOpenChangeModal && (
        <div className={styles.modal}>
          <div className={styles.actionContainer}>
            <p className={styles.modalTitle}>Підтвердіть придбання</p>
            <p className={styles.modalDescription}>
              Після підтвердження придбання з вашого бонусного рахунку буде
              списано {selectedCertificate?.point_nominal} балів.
            </p>
            <p className={styles.modalPrice}>
              Вартість сертифікату{' '}
              <b>{selectedCertificate.point_nominal} балів</b>
            </p>
            <button
              className={styles.changeButton}
              onClick={changeButtonClick}
              disabled={isBuying}
              style={isBuying ? { opacity: 0.6 } : undefined}
            >
              {isBuying ? 'Обробка...' : 'Обміняти'}
            </button>
            {buyError && (
              <p
                className={styles.modalDescription}
                style={{ color: '#ffb3b3', marginTop: 12, textAlign: 'center' }}
              >
                Не вдалося придбати сертифікат. Спробуйте ще раз.
              </p>
            )}
            <p
              className={styles.closeButton}
              onClick={() => setIsOpenChangeModal(false)}
            >
              Закрити
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateComponent;
